import type { BackendClient } from '../../platform/backendClient'
import { GeneratedColumns, isTableWithGeneratedColumns, SyncableTable, SyncableTables, SyncAction, SyncResult } from './action'
import type { OnSyncDownCompletePayload } from '../../reduxToolkit/actions/onSyncDownComplete'
import { convertServerTimestampsToISO } from '../supabase/util'
import type { SyncDigestPayload } from './syncDigest'

/**
 * One round trip to POST /backend/memberships/{id}/sync.
 *
 * Replaces syncAdd/syncUpdate/syncDelete, which each made their own PostgREST call — a
 * queue of N writes was N serialized requests, and a conflicted update was two.
 */

/**
 * What the client knows about one table: [id, updated_at] per row.
 *
 * @deprecated Superseded by SyncDigestPayload. The endpoint still accepts it while clients
 * persisted before the digest shipped are in the wild; nothing here sends it any more.
 */
export type HaveManifest = Partial<Record<SyncableTable, Array<[string, string]>>>

/**
 * Which tables the server just sent down in full, because their digest disagreed with ours.
 *
 * The rows themselves arrive in `changes`, in the same shape every other sync-down mode
 * uses — this only says which tables that payload is *complete* for, which is what lets the
 * client treat "absent from the download" as "the server does not have this row" and queue
 * an upload. On the `since` path no such claim is being made and this is empty.
 */
export type ReconciledTables = Partial<Record<SyncableTable, {
  /**
   * Whether the endpoint accepts writes to this table at all. Queueing an upload it refuses
   * would wedge the outbox, so the rule travels with the response instead of being
   * duplicated here — the client holds no copy of SyncRules.
   *
   * No table is `false` today. `phone_calls` was the one, until the AI-call screen's writes
   * moved onto the endpoint; it now accepts `add`, and `true` is right for it, because a
   * client that can *create* a row can hold one the server has never seen. Marking such a
   * table `false` would leave that row unrepairable and diverging on every pass (ADR 009).
   */
  upload: boolean
}>>

export type NeedsUpload = {
  table: SyncableTable
  id: string
  /**
   * Whether the server has any version of this row. Decides whether the client re-queues
   * an `add` or an `update`, and it is the one part of that decision the client cannot
   * work out for itself.
   */
  on_server: boolean
}

/** At most one sync-down mode per request; the endpoint refuses a request carrying two. */
export type SyncRequest = {
  actions?: SyncAction<SyncableTable>[]
  /** Sync-down fast path: the watermark the server last issued. */
  since?: string | null
  /** Sync-down full reconcile: a constant-size summary of what we hold, per table. */
  digest?: SyncDigestPayload
  /** @deprecated The previous full reconcile. Nothing sends this; see HaveManifest. */
  have?: HaveManifest
}

export type SyncResponse = {
  result?: SyncResult
  changes: OnSyncDownCompletePayload
  /** Present only for tables whose digest disagreed with the server's. */
  reconciled: ReconciledTables
  cursor?: string
}

/** One verdict per action, positionally aligned with what we sent. */
type WireResult =
  | { result: 'applied' }
  | { result: 'rejected' }
  | { result: 'error', code?: string, message?: string }

type WireResponse = {
  results?: WireResult[]
  updated_at?: string
  cursor?: string
  changes?: OnSyncDownCompletePayload
  reconciled?: ReconciledTables
}

export class SyncRequestError extends Error {
  constructor(public readonly status: number) {
    super(`sync request failed: ${status}`)
  }
}

/**
 * An error the server reported for a single action.
 *
 * Carries `code` because that is what unrecoverable.ts classifies on — a PostgREST error
 * code or a raw SQLSTATE. Without it every failure looks like a transport blip and is
 * retried forever instead of being discarded after MAX_SYNC_ATTEMPTS. Deliberately carries
 * nothing else: the server already strips row values out of its messages, and this object
 * reaches Sentry.
 */
export class SyncActionError extends Error {
  constructor(public readonly code: string | undefined, message: string) {
    super(message)
    this.name = 'SyncActionError'
  }
}

export async function postSync(
  backend: BackendClient,
  membershipId: string,
  accessToken: string,
  request: SyncRequest,
): Promise<SyncResponse> {
  const actions = (request.actions ?? []).map(toWireAction)

  const body: Record<string, unknown> = {}
  if (actions.length > 0) { body.actions = actions }
  // One mode only. The endpoint refuses a request carrying two rather than guessing, so
  // sending two here would be a 422 rather than a silent preference.
  if (request.digest !== undefined) { body.digest = request.digest }
  else if (request.have !== undefined) { body.have = request.have }
  else if (request.since !== undefined) { body.since = request.since }

  const resp = await backend.fetch(`/backend/memberships/${membershipId}/sync`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  })

  if (!resp.ok) {
    // Thrown, not returned: the sync actor's backoff is what should handle a server that
    // is down, and swallowing it here would spin the loop at full speed against it.
    throw new SyncRequestError(resp.status)
  }

  const payload = await resp.json() as WireResponse

  return {
    result: request.actions?.length
      ? toSyncResult(request.actions, payload)
      : undefined,
    changes: normalizeChanges(payload.changes),
    reconciled: payload.reconciled ?? {},
    cursor: payload.cursor,
  }
}

/**
 * Put every timestamp the server sent into the one format the store uses.
 *
 * The endpoint emits `toIso8601ZuluString('microsecond')` — six fractional digits — while
 * local writes and the realtime path both produce three. That matters because the client
 * compares `updated_at` as *strings*: replaceOrInsert, removeIfObsolete, isUnsyncable and
 * _actionsForNeededUploads all do. And `'…45.678901Z' < '…45.678Z'` is true, because '9'
 * sorts below 'Z' — so a six-digit server row reads as *older* than a three-digit local one
 * at the same instant, and replaceOrInsert's `<=` guard drops it.
 *
 * onRealtimeUpdate has always run its rows through this; the backend sync-down path did not.
 * Normalizing here rather than at each call site means one format reaches the store from all
 * three provenances, which is also what lets the digest hash stored strings directly.
 */
function normalizeChanges(changes: OnSyncDownCompletePayload | undefined): OnSyncDownCompletePayload {
  if (!changes) { return {} }

  const normalized: OnSyncDownCompletePayload = { ...changes }

  // OnSyncDownCompletePayload is an intersection of per-table shapes, so writing back
  // through a loop variable does not type-check even though the values are unchanged in
  // shape. convertServerTimestampsToISO is identity-preserving, so the rewrite is sound.
  const sections = normalized as unknown as Record<string, {
    updated?: Array<Record<string, unknown>>
    deleted?: Array<Record<string, unknown>>
  } | undefined>

  for (const table of SyncableTables) {
    const section = sections[table]
    if (!section) { continue }

    sections[table] = {
      ...(section.updated ? { updated: section.updated.map(convertServerTimestampsToISO) } : {}),
      ...(section.deleted ? { deleted: section.deleted.map(convertServerTimestampsToISO) } : {}),
    }
  }

  return normalized
}

/**
 * Strip a queued action down to what the endpoint accepts.
 *
 * Generated columns are removed for the same reason syncAdd did it: Postgres refuses to be
 * told what they are. The server drops them too, but doing it here keeps older clients and
 * newer servers agreeing about what a payload means.
 */
function toWireAction(action: SyncAction<SyncableTable>) {
  let record = action.record as any

  if (action.type !== 'delete' && isTableWithGeneratedColumns(action.table)) {
    const generated = GeneratedColumns[action.table] as readonly string[]
    record = Object.fromEntries(
      Object.entries(record).filter(([key]) => !generated.includes(key))
    )
  }

  return {
    type: action.type,
    table: action.table,
    record,
    ...(action.action_type ? { action_type: action.action_type } : {}),
  }
}

/**
 * Map the server's verdicts back onto the actions we sent, producing the exact SyncResult
 * shape the outbox reducer has always consumed.
 *
 * A missing or short `results` array is treated as an error for the actions it does not
 * cover, rather than as silent success: dropping those from the queue would lose the
 * member's writes outright, while an error only costs a retry.
 */
function toSyncResult(
  actions: SyncAction<SyncableTable>[],
  payload: WireResponse,
): SyncResult {
  const results = payload.results ?? []

  const syncResult: SyncResult = {
    updated_at: payload.updated_at ?? '',
    applied: [],
    rejected: [],
    error: [],
  }

  actions.forEach((action, i) => {
    const result = results[i]

    if (!result) {
      syncResult.error.push({
        ...action,
        error: new SyncActionError(undefined, 'no result returned for this action'),
      })
      return
    }

    switch (result.result) {
      case 'applied':
        syncResult.applied.push(action)
        break
      case 'rejected':
        syncResult.rejected.push(action)
        break
      case 'error':
        syncResult.error.push({
          ...action,
          error: new SyncActionError(result.code, result.message ?? 'sync failed'),
        })
        break
      default:
        syncResult.error.push({
          ...action,
          error: new SyncActionError(undefined, `unknown result: ${(result as any).result}`),
        })
    }
  })

  return syncResult
}
